import { memo, useCallback, useId, useMemo, useRef, useState, type ComponentProps } from 'react'
import { Flex, Text } from '@radix-ui/themes'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { AXIS_PROPS, CHART } from '~/components/Charts/tokens'
import { BAND_FILL, CHART_MARGIN, MARKER_COLOR, SERIES, SOURCE_FILL, Y_AXIS_WIDTH } from './chartStyle'
import { formatAxisLitres, formatAxisTime } from './format'
import {
  buildChartModel,
  type LeakV0ChartModel,
  type LeakV0ChartRow,
  type LeakV0PinnedTarget,
  type LeakV0TransitionMarker,
  type StationLeakV0Series
} from './chartModel'
import { zoomWindow, type LeakV0Window } from './chartWindow'
import { LeakV0ChartTooltip } from './LeakV0ChartTooltip'
import { LeakV0PinnedDetail } from './LeakV0PinnedDetail'
import { LeakV0SourceRail } from './LeakV0SourceRail'
import type { StationLeakV0Episode } from './useLeakV0Episodes'

interface StationLeakV0ChartProps {
  series: StationLeakV0Series
  episodes: StationLeakV0Episode[]
  /** The visible window, which a zoom narrows within the fetched one. */
  from: string
  to: string
  onZoom?: (range: LeakV0Window) => void
}

type ChartMouseProps = Pick<
  ComponentProps<typeof ComposedChart>,
  'onClick' | 'onMouseDown' | 'onMouseMove' | 'onMouseUp' | 'onMouseLeave'
>

/** What `ReferenceLine` hands a `shape` renderer: `y1` is the plot's bottom edge, `y2` its top. */
interface ReferenceLineShapeProps {
  x1: number
  y1: number
  y2: number
}

const MARKER_HIT_WIDTH = 12
const MARKER_TICK_HEIGHT = 10
const PARAMS_MARKER_SIZE = 6

const activeDot = (fill: string) => ({ r: 3, fill, strokeWidth: 0 })

const transitionMarkerShape =
  (marker: LeakV0TransitionMarker, onSelect: (marker: LeakV0TransitionMarker) => void) =>
  ({ x1, y1, y2 }: ReferenceLineShapeProps) => {
    const color =
      marker.event.toState === 'flap_continuation' ? MARKER_COLOR.flapContinuation : MARKER_COLOR.transition

    return (
      <g
        className="cursor-pointer"
        onClick={(event) => {
          // The chart binds its own onClick on the wrapper, so an un-stopped click also pins the bucket.
          event.stopPropagation()
          onSelect(marker)
        }}
      >
        <title>{`${marker.event.fromState ?? '—'} → ${marker.event.toState}`}</title>
        <line
          x1={x1}
          x2={x1}
          y1={y1}
          y2={y2}
          stroke={color}
          strokeWidth={1}
          strokeOpacity={0.35}
          strokeDasharray="2 3"
        />
        <line x1={x1} x2={x1} y1={y1 - MARKER_TICK_HEIGHT} y2={y1} stroke={color} strokeWidth={2} />
        <rect x={x1 - MARKER_HIT_WIDTH / 2} y={y2} width={MARKER_HIT_WIDTH} height={y1 - y2} fill="transparent" />
      </g>
    )
  }

const paramsHashMarkerShape =
  (paramsHash: string) =>
  ({ x1, y1, y2 }: ReferenceLineShapeProps) => (
    <g>
      <title>{`params hash ${paramsHash}`}</title>
      <line
        x1={x1}
        x2={x1}
        y1={y1}
        y2={y2}
        stroke={MARKER_COLOR.paramsHash}
        strokeWidth={1}
        strokeDasharray="4 3"
      />
      <rect
        x={x1 - PARAMS_MARKER_SIZE / 2}
        y={y2}
        width={PARAMS_MARKER_SIZE}
        height={PARAMS_MARKER_SIZE}
        fill={MARKER_COLOR.paramsHash}
      />
    </g>
  )

type LegendShape = 'line' | 'dashed' | 'fill' | 'rail' | 'marker'

interface LegendEntry {
  label: string
  color: string
  shape: LegendShape
}

// The swatch mirrors how the thing is drawn on the plot: a stroke reads as a rule, a band or ribbon
// as a filled block, a marker as the axis tick it is.
const LegendSwatch = ({ color, shape }: { color: string; shape: LegendShape }) => {
  if (shape === 'marker') return <span className="h-3 w-0.5 shrink-0" style={{ backgroundColor: color }} />
  if (shape === 'rail')
    return <span className="h-1 w-4 shrink-0 rounded-full" style={{ backgroundColor: color }} />
  if (shape === 'line')
    return <span className="h-0.5 w-4 shrink-0 rounded-full" style={{ backgroundColor: color }} />
  if (shape === 'dashed') {
    return (
      <span
        className="h-0.5 w-4 shrink-0"
        style={{ backgroundImage: `repeating-linear-gradient(to right, ${color} 0 3px, transparent 3px 6px)` }}
      />
    )
  }
  // Alpha fills are the point of the band tokens, so a bare swatch of one is invisible on white.
  return (
    <span
      className="h-2.5 w-4 shrink-0 rounded-sm border border-[color:var(--gray-a6)]"
      style={{ backgroundColor: color }}
    />
  )
}

const LeakV0LegendGroup = ({ title, entries }: { title: string; entries: LegendEntry[] }) => (
  <Flex align="center" gap="3" wrap="wrap">
    <Text size="1" color="gray" weight="medium" className="uppercase tracking-wider">
      {title}
    </Text>
    {entries.map((entry) => (
      <Flex key={entry.label} align="center" gap="1">
        <LegendSwatch color={entry.color} shape={entry.shape} />
        <Text size="1" color="gray">
          {entry.label}
        </Text>
      </Flex>
    ))}
  </Flex>
)

const LeakV0Legend = ({ shaypAvailable }: { shaypAvailable: boolean }) => {
  const seriesEntries: LegendEntry[] = [
    { ...SERIES.consumption, shape: 'fill' },
    { ...SERIES.willieLeak, shape: 'line' },
    ...(shaypAvailable ? [{ ...SERIES.shaypLeak, shape: 'line' as const }] : []),
    { ...SERIES.frozenBaseline, shape: 'dashed' },
    { ...SERIES.noiseRibbon, shape: 'fill' }
  ]

  const overlayEntries: LegendEntry[] = [
    { label: 'CANDIDATE', color: BAND_FILL.CANDIDATE, shape: 'fill' },
    { label: 'CONFIRMED', color: BAND_FILL.CONFIRMED, shape: 'fill' },
    { label: 'PENDING_CLEAR', color: BAND_FILL.PENDING_CLEAR, shape: 'fill' },
    { label: 'Rail · backfill', color: SOURCE_FILL.backfill, shape: 'rail' },
    { label: 'Rail · live', color: SOURCE_FILL.live, shape: 'rail' },
    { label: 'Transition', color: MARKER_COLOR.transition, shape: 'marker' },
    { label: 'Anti-flap reopen', color: MARKER_COLOR.flapContinuation, shape: 'marker' },
    { label: 'Params change', color: MARKER_COLOR.paramsHash, shape: 'marker' }
  ]

  return (
    <Flex direction="column" gap="2" pt="1">
      <LeakV0LegendGroup title="Series" entries={seriesEntries} />
      <LeakV0LegendGroup title="Overlays" entries={overlayEntries} />
    </Flex>
  )
}

interface DragState {
  anchorMs: number
  anchorX: number
  releaseMs: number
  moved: boolean
}

interface ZoomDrag {
  chartHandlers: ChartMouseProps
  previewX: { fromX: number; toX: number } | null
}

const DRAG_THRESHOLD_PX = 4

const useZoomDrag = (
  model: LeakV0ChartModel,
  onPin: (row: LeakV0ChartRow) => void,
  onZoom: ((range: LeakV0Window) => void) | undefined
): ZoomDrag => {
  const [previewX, setPreviewX] = useState<ZoomDrag['previewX']>(null)
  const drag = useRef<DragState | null>(null)
  // Written at mouse-up and read by the click that follows it: recharts' wrapper onClick fires at the
  // end of any drag ending inside the plot, which would otherwise pin a bucket as well as zoom.
  const zoomed = useRef(false)

  const chartHandlers = useMemo<ChartMouseProps>(() => {
    const rowAt = (index: number | undefined) => (index === undefined ? undefined : model.rows[index])

    return {
      onMouseDown: ({ activeTooltipIndex, chartX }) => {
        const anchor = rowAt(activeTooltipIndex)
        if (!anchor || chartX === undefined) return
        drag.current = { anchorMs: anchor.atMs, anchorX: chartX, releaseMs: anchor.atMs, moved: false }
      },
      onMouseMove: ({ activeTooltipIndex, chartX }) => {
        const current = drag.current
        const release = rowAt(activeTooltipIndex)
        if (!current || !release || chartX === undefined) return

        current.releaseMs = release.atMs
        current.moved = current.moved || Math.abs(chartX - current.anchorX) >= DRAG_THRESHOLD_PX
        if (current.moved) setPreviewX({ fromX: current.anchorX, toX: chartX })
      },
      onMouseUp: () => {
        const current = drag.current
        drag.current = null
        setPreviewX(null)
        zoomed.current = current?.moved ?? false
        if (current?.moved) onZoom?.(zoomWindow(current.anchorMs, current.releaseMs, model))
      },
      // A release outside the plot never reaches onMouseUp, so leaving abandons the selection.
      onMouseLeave: () => {
        drag.current = null
        setPreviewX(null)
      },
      onClick: ({ activeTooltipIndex }) => {
        if (zoomed.current) {
          zoomed.current = false
          return
        }
        const row = rowAt(activeTooltipIndex)
        if (row) onPin(row)
      }
    }
  }, [model, onPin, onZoom])

  return { chartHandlers, previewX }
}

interface LeakV0ChartCanvasProps {
  model: LeakV0ChartModel
  chartHandlers: ChartMouseProps
  onPinTransition: (marker: LeakV0TransitionMarker) => void
}

// Memoized on the model: pinning re-renders the detail panel, never the ~2,000-point plot.
const LeakV0ChartCanvas = memo(({ model, chartHandlers, onPinTransition }: LeakV0ChartCanvasProps) => {
  const gradientId = useId()

  return (
    <div className="h-96 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={model.rows} margin={CHART_MARGIN} {...chartHandlers}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SERIES.consumption.color} stopOpacity={CHART.areaFillOpacity * 2} />
              <stop offset="100%" stopColor={SERIES.consumption.color} stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Bands sit behind the series and the transition markers in front: z-order is child
              order, `isFront` is inert. */}
          {model.bands.map((band) => (
            <ReferenceArea
              key={band.key}
              x1={band.fromMs}
              x2={band.toMs}
              fill={BAND_FILL[band.kind]}
              fillOpacity={1}
              stroke="none"
              ifOverflow="hidden"
            />
          ))}

          {model.paramsChanges.map((marker) => (
            <ReferenceLine key={marker.key} x={marker.atMs} shape={paramsHashMarkerShape(marker.paramsHash)} />
          ))}

          <CartesianGrid strokeDasharray={CHART.gridDash} stroke={CHART.gridStroke} vertical={false} />
          <XAxis
            dataKey="atMs"
            type="number"
            scale="time"
            domain={[model.fromMs, model.toMs]}
            ticks={model.axisTicks}
            tickFormatter={formatAxisTime}
            {...AXIS_PROPS}
            minTickGap={8}
          />
          <YAxis {...AXIS_PROPS} width={Y_AXIS_WIDTH} tickFormatter={formatAxisLitres} />
          <Tooltip
            content={
              <LeakV0ChartTooltip
                rowsByTime={model.rowsByTime}
                bands={model.bands}
                shaypAvailable={model.shaypAvailable}
              />
            }
            cursor={{ stroke: CHART.cursorStroke, strokeDasharray: CHART.cursorDash }}
            isAnimationActive={false}
          />

          <Area
            dataKey="consumptionL"
            name={SERIES.consumption.label}
            stroke={SERIES.consumption.color}
            strokeWidth={CHART.strokeWidth}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={activeDot(SERIES.consumption.color)}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Area
            dataKey="noiseRibbonL"
            name={SERIES.noiseRibbon.label}
            stroke="none"
            fill={SERIES.noiseRibbon.color}
            fillOpacity={1}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
            connectNulls={false}
            tooltipType="none"
            legendType="none"
          />
          <Line
            dataKey="frozenBaselineL"
            name={SERIES.frozenBaseline.label}
            stroke={SERIES.frozenBaseline.color}
            strokeWidth={CHART.strokeWidth}
            strokeDasharray="5 4"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
          {model.shaypAvailable ? (
            <Line
              dataKey="shaypLeakL"
              name={SERIES.shaypLeak.label}
              stroke={SERIES.shaypLeak.color}
              strokeWidth={CHART.strokeWidth}
              dot={false}
              activeDot={activeDot(SERIES.shaypLeak.color)}
              isAnimationActive={false}
              connectNulls={false}
            />
          ) : null}
          <Line
            dataKey="leakL"
            name={SERIES.willieLeak.label}
            stroke={SERIES.willieLeak.color}
            strokeWidth={CHART.strokeWidth}
            dot={false}
            activeDot={activeDot(SERIES.willieLeak.color)}
            isAnimationActive={false}
            connectNulls={false}
          />

          {model.transitions.map((marker) => (
            <ReferenceLine
              key={marker.key}
              x={marker.atMs}
              shape={transitionMarkerShape(marker, onPinTransition)}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
})

interface LeakV0ChartPlotProps {
  model: LeakV0ChartModel
  onPinBucket: (row: LeakV0ChartRow) => void
  onPinTransition: (marker: LeakV0TransitionMarker) => void
  onZoom: ((range: LeakV0Window) => void) | undefined
}

// The drag preview repaints on every mousemove, so it is isolated here: the memoized canvas bails
// out and the rail, legend and pinned detail never re-render for a gesture that cannot move them.
const LeakV0ChartPlot = ({ model, onPinBucket, onPinTransition, onZoom }: LeakV0ChartPlotProps) => {
  const { chartHandlers, previewX } = useZoomDrag(model, onPinBucket, onZoom)

  return (
    <div className="relative select-none">
      <LeakV0ChartCanvas model={model} chartHandlers={chartHandlers} onPinTransition={onPinTransition} />
      {previewX ? (
        // A DOM strip rather than a `ReferenceArea`: `chartX` is measured from the recharts wrapper,
        // which starts at this box's origin, so no scale lookup is needed to place it.
        <div
          className="pointer-events-none absolute inset-y-0 bg-[var(--gray-a5)]"
          style={{
            left: Math.min(previewX.fromX, previewX.toX),
            width: Math.abs(previewX.toX - previewX.fromX)
          }}
        />
      ) : null}
    </div>
  )
}

export const StationLeakV0Chart = ({ series, episodes, from, to, onZoom }: StationLeakV0ChartProps) => {
  const [pinned, setPinned] = useState<LeakV0PinnedTarget | null>(null)
  const model = useMemo(() => buildChartModel(series, episodes, from, to), [series, episodes, from, to])

  const pinBucket = useCallback((row: LeakV0ChartRow) => setPinned({ kind: 'bucket', row }), [])

  const pinTransition = useCallback(
    (marker: LeakV0TransitionMarker) => setPinned({ kind: 'transition', marker }),
    []
  )

  return (
    <Flex direction="column" gap="4">
      <LeakV0ChartPlot model={model} onPinBucket={pinBucket} onPinTransition={pinTransition} onZoom={onZoom} />
      <LeakV0SourceRail runs={model.sourceRuns} fromMs={model.fromMs} toMs={model.toMs} />
      <LeakV0Legend shaypAvailable={model.shaypAvailable} />
      {pinned ? <LeakV0PinnedDetail target={pinned} onClose={() => setPinned(null)} /> : null}
    </Flex>
  )
}
